import { 
    useState,
    useEffect,
} from 'react';
import { 
    useSelector, 
    useDispatch, 
} from 'react-redux';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useNavigate } from "react-router-dom";
import {  
    Box, 
    TextField, 
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    Popper,
} from '@mui/material';
import { styled } from "@mui/system";
import { CancelRounded } from '@mui/icons-material';
import { withPather } from 'react-pather';
import { PrimaryButton } from '../../../general';
import Loader from '../../../Loader';
import { 
    PasswordFieldExternalLabel, 
    Confirmation, 
    ColorPicker,
} from '../../../general';
import { DEFAULT_PRIMARY_COLOR } from '../../../../config';
import { useToken } from '../../../../hooks';
import { validateEmail } from '../../../../utils';
import { setUser, unSetUser } from '../../../../slices/userSlice';
import { setTheme as setClientTheme } from '../../../../slices/themeSlice';

const Field = ({ label, value, type, onChange }) => {
    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
            <Box>
                <span>
                    {label}
                </span>
            </Box>
            <TextField 
                value={value} 
                type={type} 
                variant="outlined" 
                sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                        borderRadius: '12px',
                    },
                }} 
                onChange={onChange}
            />
        </Box>
    );
}

const RoundedPasswordFieldExternalLabel = props => {
    return (
        <PasswordFieldExternalLabel 
            {...props}
            style={{
                '& .MuiInputBase-root': {
                    borderRadius: '12px',
                },
            }}
        />
    )
}

const General = ({ pather }) => {

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { removeToken } = useToken();
    
    const user = useSelector(state => state.user.value);

    const [loading, setLoading] = useState(false);
    const [username, setUsername] = useState(user?.username);
    const [email, setEmail] = useState(user?.email);
    const [openConfirmDeactivate, setOpenConfirmDeactivate] = useState(false);

    const [password, setPassword] = useState(null);
    const [newPassword, setNewPassword] = useState(null);
    const [newPasswordConfirmation, setNewPasswordConfirmation] = useState(null);

    // Metadata
    const [theme, setTheme] = useState('');
    const [timeZone, setTimeZone] = useState('');
    const [timeZoneItems, setTimeZoneItems] = useState([]);

    // Password visibility
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showNewPasswordConfirmation, setShowNewPasswordConfirmation] = useState(false);

    const handleClickShowPassword = () => setShowPassword(show => !show);
    const handleClickShowNewPassword = () => setShowNewPassword(show => !show);
    const handleClickShowNewPasswordConfirmation = () => setShowNewPasswordConfirmation(show => !show);
    //

    const onUsernameChange = e => {
        const value = e.target.value;
        setUsername(value);
    }

    const onEmailChange = e => {
        const value = e.target.value;
        setEmail(value);
    }

    const validateFields = () => {
        if (!username) {
            enqueueSnackbar('Username field cannot be empty', { variant: 'error' });
            return false;
        }
        else if (!email) {
            enqueueSnackbar('Email field cannot be empty', { variant: 'error' });
            return false;
        }
        else if(!validateEmail(email)){
            enqueueSnackbar('Email address is invalid', { variant: 'error' });
            return false;
        } 
        else if((newPassword && newPassword !== newPasswordConfirmation) || (newPasswordConfirmation && !newPassword)){
            enqueueSnackbar('Passwords do not match', { variant: 'error' });
            return false;
        } 
        else if(newPassword && newPassword.length < 8){
            enqueueSnackbar('Password length must be at least 8 characters', { variant: 'error' });
            return false;
        }
        return true;
    }

    const update = () => {
        setLoading(true);
        if(!validateFields()) {
            setLoading(false);
            return;
        }
        axios.put(
            pather.back.Settings.user, 
            {
                username,
                email,
                password,
                newPassword,
            },    
        )
        .then(response => {
            const data = response.data;
            dispatch(setUser({
                isLogined: true,
                email: data.email,
                username: data.username,
                isConfirmed: data.is_confirmed,
            }));
            enqueueSnackbar(data.status, { variant: 'success' });
            setPassword(null);
            setNewPassword(null);
            setNewPasswordConfirmation(null);
        })
        .catch(response => {
            const data = response?.response?.data;
            enqueueSnackbar(data?.status ? data?.status : 'Invalid input!', { variant: 'error' });
            console.log(response);
        })
        .finally(() => {
            setLoading(false);
        });
    }

    const deactivate = () => {
        setLoading(true);
        axios.post(pather.back.Settings.User.deactivate)
        .then(response => {
            const data = response.data;
            enqueueSnackbar(data.status, { variant: 'success' });
            // Logout user
            axios.post(pather.back.Auth.logout)
            .then(() => {
                removeToken();
                dispatch(unSetUser());
                navigate('/');
            })
            .catch(response => {
                const data = response?.response?.data;
                data?.status && enqueueSnackbar(data.status, { variant: 'error' });
                console.log(response);
            })
        })
        .catch(response => {
            const data = response?.response?.data;
            data?.status && enqueueSnackbar(data.status, { variant: 'error' });
            console.log(response);
        })
        .finally(() => {
            setLoading(false);
        });
    }

    const fetchMetadata = () => {
        setLoading(true);
        axios.get(pather.back.Settings.User.metadata)
        .then(response => {
            const data = response.data;
            setTimeZoneItems(data?.all_timezones);
            setTimeZone(data?.user_timezone);
            setTheme(data?.user_theme);
        })
        .catch(error => {
            console.log(error);
            const status = error?.response?.data?.status;
            status && enqueueSnackbar(status, { variant: 'error' });
        }).finally(() => {
            setLoading(false);
        });
    }

    useEffect(() => {
        fetchMetadata();
    }, []);

    const handleChangeTheme = event => {
        const newValue = event.target.value;
        const onSuccess = () => {
            setTheme(newValue);
            dispatch(setClientTheme(newValue))
        };
        updateMetadata('theme', newValue, onSuccess);
    }

    const handleChangeTimeZone = (event, newValue) => {
        const onSuccess = () => setTimeZone(newValue);
        updateMetadata('timeZone', newValue, onSuccess);
    }
    
    const handleChangePrimaryColor = updatedColor => {
        const onSuccess = () => dispatch(setUser({primaryColor: updatedColor}));
        updateMetadata('primaryColor', updatedColor, onSuccess);
    }

    const updateMetadata = (key, newValue, onSuccess) => {
        if (!newValue) return;
        setLoading(true);
        axios.put(
            pather.back.Settings.User.metadata, 
            {
                [key]: newValue,
            },
        )
        .then(response => {
            onSuccess();
        })
        .catch(err => {
            console.log(err);
            const data = err?.response?.data;
            enqueueSnackbar(data?.status ? data?.status : 'Some error occurred', { variant: 'error' });
        })
        .finally(() => {
            setLoading(false);
        });
    }

    const width = {xs: '100%', sm: '70%', md: '40vw', lg: '500px'}

    return (
        <>
            {
                loading ? 
                <Loader/> :
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
                    <Box sx={{width: '100%', display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 2}}>
                        <Box sx={{width: width, display: 'flex', flexDirection: 'column', gap: 2}}>
                            <Field 
                                label='Username: ' 
                                value={username} 
                                type="text"
                                onChange={onUsernameChange}
                            />
                            <Field 
                                label='Email: ' 
                                value={email} 
                                type="text"
                                onChange={onEmailChange}
                            />
                            <RoundedPasswordFieldExternalLabel 
                                externalLabel='Current Password: ' 
                                value={password}
                                handleChange={e => setPassword(e.target.value)} 
                                show={showPassword}
                                handleShow={handleClickShowPassword}
                            />
                            <RoundedPasswordFieldExternalLabel
                                externalLabel='New Password: '
                                value={newPassword}
                                handleChange={e => setNewPassword(e.target.value)} 
                                show={showNewPassword}
                                handleShow={handleClickShowNewPassword}
                            />
                            <RoundedPasswordFieldExternalLabel
                                externalLabel='Confirm New Password: '
                                value={newPasswordConfirmation}
                                handleChange={e => setNewPasswordConfirmation(e.target.value)} 
                                show={showNewPasswordConfirmation}
                                handleShow={handleClickShowNewPasswordConfirmation}
                                error={newPasswordConfirmation && newPassword !== newPasswordConfirmation }
                                helperText={newPasswordConfirmation && newPassword !== newPasswordConfirmation && "Passwords do not match"}
                            />
                        </Box>
                        <Box sx={{width: width, display: 'flex', justifyContent: 'flex-end', gap: 1}}>
                            <PrimaryButton 
                                text='Submit' 
                                styles={{
                                    width: '125px',
                                    padding: "8px 24px",
                                    borderRadius: '30px',
                                }}
                                onClick={update} 
                            />
                        </Box>
                    </Box>
                    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                        <Box sx={{width: width, display: 'flex', flexDirection: 'column', gap: 1}}>
                            <Box sx={{
                                display: 'flex', 
                                justifyContent: 'center',
                            }}>
                                <h2>
                                    Defaults
                                </h2>
                            </Box>
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
                                <Box sx={{
                                    display: 'flex', 
                                    gap: 3
                                }}>
                                    <ThemeSelect 
                                        label='Theme'
                                        value={theme}
                                        handleChange={handleChangeTheme}
                                        items={['dark', 'light']}
                                    />
                                    <TimezoneAutocomplete 
                                        label='Time Zone'
                                        value={timeZone}
                                        handleChange={handleChangeTimeZone}
                                        items={timeZoneItems}
                                    />
                                </Box>
                                <PrimaryColorPicker
                                    handleChangePrimaryColor={handleChangePrimaryColor}
                                />
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{
                        display: 'flex', 
                        justifyContent: 'center',
                    }}> 
                        <Box 
                            sx={{
                                width: width,
                                borderTop: '0.3px solid #AAB4BE',
                                mt: 5, pt: 5,
                            }}
                        >
                            <Button 
                                variant="outlined" 
                                color="error" 
                                startIcon={<CancelRounded />}
                                sx={{
                                    width: '100%',
                                    pt: 1.5,
                                    pb: 1.5,
                                    textTransform: "none",
                                    fontWeight: 'bold',
                                    borderRadius: '30px',
                                }}
                                onClick={() => setOpenConfirmDeactivate(true)}
                            >
                                Deactivate Account
                            </Button>
                        </Box>
                    </Box>
                    <Confirmation 
                        open={openConfirmDeactivate}
                        setOpen={setOpenConfirmDeactivate}
                        title="Account deactivation"
                        description={
                            `Are you sure you want to deactivate your account? This action can not be reverted.`
                        }
                        success={deactivate}
                    />
                </Box>
            }
        </>
    );
}

const colorPickerPresetColors =  [
    DEFAULT_PRIMARY_COLOR, 
    "#CB3E50", 
    "#88304E", 
    "#387478", 
    "#D16044",
    "#6A9C89", 
    "#FFFFFF",
    "#000000",
];

const PrimaryColorPicker = ({ handleChangePrimaryColor }) => {

    const user = useSelector(state => state.user.value);
    const [color, setColor] = useState(user.primaryColor);

    useEffect(() => {
        if (color === user.primaryColor) return;
        handleChangePrimaryColor(color);
    }, [color, user.primaryColor, handleChangePrimaryColor]);

    return (
        <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
            <Box>
                Primary color: 
            </Box>
            <ColorPicker 
                color={color}
                setColor={setColor}
                presetColors={colorPickerPresetColors}
            />
        </Box>
    );
}

const ThemeSelect = ({ label, value, handleChange, items }) => {  
    return (
      <Box sx={{ minWidth: {xs: 100, sm: 130 }}}> 
        <FormControl variant="standard" fullWidth>
          <InputLabel id={`${label}-select-label`}>{label}</InputLabel>
          <Select
            labelId={`${label}-select-label`}
            id={`${label}-select`}
            value={value}
            label={label}
            onChange={handleChange}
          >
            {
                items.map((value, index) => (
                    <MenuItem value={value} key={index}>{value}</MenuItem>
                ))
            }
          </Select>
        </FormControl>
      </Box>
    );
}

const CustomPopper = styled(Popper)(({ theme }) => ({
    "& .MuiAutocomplete-listbox": {
        backgroundColor: theme.palette.background.paper, // Ensure background matches theme
        color: theme.palette.text.primary, // Text color
        "&::-webkit-scrollbar": {
            width: "8px", // Adjust scrollbar width
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.mode === "light" ? "#ccc" : "#555", // Light theme: Gray, Dark theme: Darker gray
            borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": {
            backgroundColor: theme.palette.mode === "light" ? "#f0f0f0" : "#333", // Light theme: Light gray, Dark theme: Dark gray
        },
    },
}));

const TimezoneAutocomplete = ({ label, value, handleChange, items }) => {
    return (
        <Autocomplete
            id={`${label}-select`}
            sx={{ width: '100%' }}
            options={items}
            autoHighlight
            value={value}
            onChange={handleChange}
            PopperComponent={CustomPopper}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant='standard'
                />
            )}
        />
    );
};

export default withPather()(General);